import {
  closestCenter,
  DndContext,
  DragEndEvent,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import React, { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, Checkbox, Form, InlineLoading, InlineNotification, Stack, TextInputSkeleton } from '@carbon/react';
import { Blog, Draggable, Help, Home, Image, Information, Restaurant, Store } from '@carbon/react/icons';
import { useClearSavedState } from 'src/hooks/useClearSavedState';
import { useStorefront, useUpdateStorefrontMutation } from 'src/hooks/useStorefront';
import { StorefrontUpdateDto } from 'src/services/apis/types';

/** The fixed seven, in the API's canonical order (`storefront.enum.ts` on the server). That order
 * is also what the server's `resolveSections` falls back to for any key missing from the array. */
export const STOREFRONT_SECTION_KEYS = ['home', 'menu', 'about', 'branches', 'news', 'gallery', 'faqs'] as const;

export type StorefrontSectionKey = (typeof STOREFRONT_SECTION_KEYS)[number];

/** The generated entity types `sections` as `string[]` because Swagger could not describe the
 * jsonb column's items; this is the real shape. */
export type StorefrontSection = { key: StorefrontSectionKey; visible: boolean };

/** What each row shows: the label, one line on what the section is, and its icon. */
const SECTION_DISPLAY: Record<StorefrontSectionKey, { label: string; description: string; icon: typeof Home }> = {
  home: { label: 'storefront.sections.home', description: 'storefront.sections.homeLocked', icon: Home },
  menu: { label: 'storefront.sections.menu', description: 'storefront.sections.menuDescription', icon: Restaurant },
  about: { label: 'storefront.sections.about', description: 'storefront.sections.aboutDescription', icon: Information },
  branches: {
    label: 'storefront.sections.branches',
    description: 'storefront.sections.branchesDescription',
    icon: Store,
  },
  news: { label: 'storefront.sections.news', description: 'storefront.sections.newsDescription', icon: Blog },
  gallery: { label: 'storefront.sections.gallery', description: 'storefront.sections.galleryDescription', icon: Image },
  faqs: { label: 'storefront.sections.faqs', description: 'storefront.sections.faqsDescription', icon: Help },
};

function isSectionKey(value: unknown): value is StorefrontSectionKey {
  return STOREFRONT_SECTION_KEYS.includes(value as StorefrontSectionKey);
}

/**
 * Normalises whatever the row holds — `null` for a storefront that has never been saved, a
 * hand-edited jsonb, or the API's already-resolved list — into the complete ordered seven.
 *
 * The rules are deliberately the server's own (`resolveSections`, Plan 01 Task 4): unknown keys
 * and duplicates are dropped, and every missing key is appended in canonical order. Matching it
 * matters because the update DTO replaces `sections` wholesale — a partial list would let the
 * server fill the gaps in ITS order and silently discard the arrangement the owner just made.
 */
export function toSectionList(input: unknown): StorefrontSection[] {
  const stored = Array.isArray(input) ? input : [];
  const seen = new Set<StorefrontSectionKey>();
  const resolved: StorefrontSection[] = [];

  for (const candidate of stored) {
    const key = (candidate as StorefrontSection | null)?.key;
    if (!isSectionKey(key) || seen.has(key)) {
      continue;
    }
    seen.add(key);
    resolved.push({ key, visible: (candidate as StorefrontSection).visible !== false });
  }

  for (const key of STOREFRONT_SECTION_KEYS) {
    if (!seen.has(key)) {
      resolved.push({ key, visible: true });
    }
  }

  return resolved;
}

/**
 * The whole of what a drop does, kept pure because the drag itself cannot run in jsdom: @dnd-kit
 * needs layout and pointer geometry a headless DOM does not provide. `arrayMove` supplies the
 * same index arithmetic the sortable strategy assumes.
 */
export function moveSection(sections: StorefrontSection[], activeKey: string, overKey: string): StorefrontSection[] {
  const from = sections.findIndex((section) => section.key === activeKey);
  const to = sections.findIndex((section) => section.key === overKey);
  if (from === -1 || to === -1 || from === to) {
    return sections;
  }
  return arrayMove(sections, from, to);
}

interface SectionRowProps {
  section: StorefrontSection;
  onToggle: (key: StorefrontSectionKey, visible: boolean) => void;
}

const SectionRow = React.memo(({ section, onToggle }: SectionRowProps) => {
  const { t } = useTranslation();
  // `home` is the storefront's front page, so it is not the owner's to hide. The API's
  // `resolveSections` also re-appends a `home` MISSING from the array as visible, so the toggle
  // would look like it saved and then be gone on the next read — hence disabled, not toggled.
  const locked = section.key === 'home';
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: section.key });
  const display = SECTION_DISPLAY[section.key];
  const Icon = display.icon;
  const label = t(display.label);

  return (
    <li
      ref={setNodeRef}
      data-section-key={section.key}
      className={`storefront-sections__row${isDragging ? ' storefront-sections__row--dragging' : ''}`}
      style={{ transform: CSS.Translate.toString(transform), transition }}
    >
      <button
        type="button"
        className={'storefront-sections__handle'}
        aria-label={t('storefront.sections.reorder')}
        {...attributes}
        {...listeners}
      >
        <Draggable size={16} />
      </button>
      <span className={'storefront-sections__icon'} aria-hidden>
        <Icon size={20} />
      </span>
      <div className={'storefront-sections__text'}>
        <span className={'storefront-sections__title'}>{label}</span>
        <span className={'storefront-sections__description'}>{t(display.description)}</span>
      </div>
      {/* The title above is the visible label; the checkbox carries the same one for screen readers. */}
      <Checkbox
        id={`storefront-section-${section.key}`}
        labelText={label}
        hideLabel
        checked={section.visible}
        disabled={locked}
        onChange={(_event, { checked }) => onToggle(section.key, checked)}
      />
    </li>
  );
});

SectionRow.displayName = 'SectionRow';

interface SectionsEditorProps {
  sections: StorefrontSection[];
  saving: boolean;
  saved: boolean;
  error: unknown;
  onSave: (payload: StorefrontUpdateDto) => void;
}

const SectionsEditor = React.memo(({ sections: stored, saving, saved, error, onSave }: SectionsEditorProps) => {
  const { t } = useTranslation();
  // Seeded once from the loaded row and owned from then on: dragging and toggling are local
  // until Save, and the refetch a save triggers must not yank the list around mid-interaction.
  const [sections, setSections] = useState(stored);
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 4 } }),
    // Space lifts, arrows move, space drops — the same keyboard path the issue board offers.
    useSensor(KeyboardSensor, { coordinateGetter: sortableKeyboardCoordinates })
  );

  const handleDragEnd = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    if (!over) {
      return;
    }
    setSections((current) => moveSection(current, String(active.id), String(over.id)));
  }, []);

  const handleToggle = useCallback((key: StorefrontSectionKey, visible: boolean) => {
    setSections((current) => current.map((section) => (section.key === key ? { ...section, visible } : section)));
  }, []);

  const handleSubmit = useCallback(
    (event: React.FormEvent<HTMLFormElement>) => {
      event.preventDefault();
      // The WHOLE array every time: PATCH replaces `sections` wholesale (Plan 01, Task 4), so
      // sending only the row the user touched would drop the other six.
      onSave({ sections });
    },
    [onSave, sections]
  );

  return (
    <Form onSubmit={handleSubmit}>
      <Stack gap={5}>
        {/* `closestCenter`, not the board's `closestCorners`: this is one vertical list, and
            corner distance makes the target flicker between neighbours mid-drag. */}
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext items={sections.map((section) => section.key)} strategy={verticalListSortingStrategy}>
            <ul className={'storefront-sections__list'}>
              {sections.map((section) => (
                <SectionRow key={section.key} section={section} onToggle={handleToggle} />
              ))}
            </ul>
          </SortableContext>
        </DndContext>

        <div className={'storefront-sections__actions'}>
          {saving || saved ? (
            <InlineLoading
              description={saved ? t('common.saved') : `${t('common.saving')}...`}
              status={saved ? 'finished' : 'active'}
            />
          ) : (
            <Button type="submit">{t('storefront.actions.save')}</Button>
          )}
        </div>

        {error ? <InlineNotification kind="error" subtitle={t('messages.somethingWentWrong')} /> : null}
      </Stack>
    </Form>
  );
});

SectionsEditor.displayName = 'SectionsEditor';

const StorefrontSections = React.memo(() => {
  const { t } = useTranslation();
  const { data: storefront, isLoading } = useStorefront();
  const {
    mutate: updateStorefront,
    isLoading: saving,
    isSuccess: saved,
    error: updateError,
    reset: resetUpdate,
  } = useUpdateStorefrontMutation();

  useClearSavedState(saved, resetUpdate);

  return (
    <Stack gap={6}>
      <p className={'storefront-sections__hint'}>{t('storefront.sections.hint')}</p>
      {isLoading && <TextInputSkeleton />}
      {!isLoading && storefront && (
        <SectionsEditor
          sections={toSectionList(storefront.sections)}
          saving={saving}
          saved={saved}
          error={updateError}
          onSave={updateStorefront}
        />
      )}
    </Stack>
  );
});

export default StorefrontSections;
